<script lang="ts">
	import Router from 'svelte-spa-router';
	import { GrpcWebFetchTransport } from '@protobuf-ts/grpcweb-transport';
	import { AppApiClient } from './api_contracts/app.client';
	import { getTokenStorage, setTokenStorage } from './utils/localStorage';
	import { API_URL } from './utils/config';
	import { appStore } from './stores/appStore';
	import { themeStore } from './stores/themeStore';

	// Import pages
	import Home from './pages/Home.svelte';
	import Tests from './pages/Tests.svelte';
	import Login from './pages/Login.svelte';
	import Register from './pages/Register.svelte';
	import VerifyEmail from './pages/VerifyEmail.svelte';
	import ForgotPassword from './pages/ForgotPassword.svelte';
	import ResetPassword from './pages/ResetPassword.svelte';
	import AlreadyLoggedIn from './pages/AlreadyLoggedIn.svelte';
	import Profile from './pages/Profile.svelte';
	import UserProfile from './pages/PublicUserProfile.svelte';
	import RepositoryList from './pages/RepositoryList.svelte';
	import StarredRepositories from './pages/StarredRepositories.svelte';
	import Repository from './pages/Repository.svelte';
	import NewRepository from './pages/NewRepository.svelte';
	import ImportProject from './pages/ImportProject.svelte';
  import RepositoryCodeView from './pages/RepositoryCodeView.svelte';
	import Branches from './pages/Branches.svelte';
	import Notifications from './pages/Notifications.svelte';
	import ForkRepository from './pages/ForkRepository.svelte';
	import TermsAndConditions from './pages/TermsAndConditions.svelte';
	import PrivacyPolicy from './pages/PrivacyPolicy.svelte';
	import Billing from './pages/Billing.svelte';
  import Index from './pages/Index.svelte';
	import Activity from './pages/Activity.svelte';
	import Dashboard from './pages/Dashboard.svelte';
	import Admin from './pages/Admin.svelte';
	import AdminUsers from './pages/admin/AdminUsers.svelte';
	import AdminRepositories from './pages/admin/AdminRepositories.svelte';
	import AdminSessions from './pages/admin/AdminSessions.svelte';
	import AdminPayments from './pages/admin/AdminPayments.svelte';
	import AdminSearch from './pages/admin/AdminSearch.svelte';
	import Deployments from './pages/Deployments.svelte';
	import NewDeployment from './pages/NewDeployment.svelte';
	import Databases from './pages/Databases.svelte';
	import NewDatabase from './pages/NewDatabase.svelte';
	import OAuthCallback from './pages/OAuthCallback.svelte';

	// Define routes
	// NOTE: Order matters! Specific routes must come before wildcard routes
	const routes = {
		'/': Home,
		'/app': Index,
		'/tests': Tests,
		'/login': Login,
		'/register': Register,
		'/verify-email': VerifyEmail,
		'/auth/google/callback': OAuthCallback,
		'/auth/github/callback': OAuthCallback,
		'/terms-and-conditions': TermsAndConditions,
		'/privacy-policy': PrivacyPolicy,
		'/forgot-password': ForgotPassword,
		'/reset-password': ResetPassword,
		'/already-logged-in': AlreadyLoggedIn,
		'/profile': Profile,
		'/billing': Billing,
		'/activity': Activity,
		'/admin': Admin,
		'/dashboard': Dashboard,
		'/dashboard/users': AdminUsers,
		'/dashboard/repositories': AdminRepositories,
		'/dashboard/sessions': AdminSessions,
		'/dashboard/payments': AdminPayments,
		'/dashboard/search': AdminSearch,
		'/stars': StarredRepositories,
		'/notifications': Notifications,
		'/deployments/new': NewDeployment,
		'/deployments': Deployments,
		'/databases/new': NewDatabase,
		'/databases': Databases,
		'/repositories': RepositoryList,
		'/repositories/new': NewRepository,
		'/import': ImportProject,
		'/users/:username/repositories/:name/fork': ForkRepository,
		'/users/:username/repositories/:name/files/*': RepositoryCodeView,
		'/users/:username/repositories/:name/branches': Branches,
		'/users/:username/repositories/:name': Repository,
		'/users/:username': UserProfile,
	};

	// Initialize theme
	$effect(() => {
		themeStore.initialize();
	});

	// Initialize gRPC client
	$effect(() => {
		let client
		try {
			const token = getTokenStorage();
			appStore.setToken(token ?? '');
			console.log('Token from localStorage:', token);
			console.log('GRPC URL :', API_URL);

			// Create transport and client
			const transport = new GrpcWebFetchTransport({
				baseUrl: API_URL,
			});

			// Create the AppApi client
			 client = new AppApiClient(transport);

			appStore.setClient(client);
			console.log('gRPC client initialized');

			const storedToken = getTokenStorage();            



			 if (storedToken) {  
				
				(async () => {           
					const result = await client.validateToken({ token });
					 console.log('Token from localStorage:', storedToken);       
					if (result.response.successFull) {

						appStore.setToken(storedToken);
						await validateAndFetchProfile(client, storedToken);   
						console.log('Token is valid.');
					} else {
						console.log('Token is invalid.');
						setTokenStorage(undefined);
						appStore.setToken('');

						// Redirect to login
		window.location.hash = '/login';
				
				                                                                         
                
					}
					                                           
				})();
            } else {                                                                                            
                 appStore.setToken(''); // Ensure token is empty in store if not found in storage                
             }     
		} catch (e) {
			console.error('Error initializing gRPC client:', e);
		}
	});

	 async function validateAndFetchProfile(client: AppApiClient, token: string) {
        try {
            // This call implicitly validates the token and fetches the profile
            const result = await client.getUserProfile({ token });
            if (result.response.response.successFull && result.response.profile) {
                appStore.setToken(token); // Set token in store
                appStore.setProfile(result.response.profile); // Set profile in store

                // Sync theme from user profile
                const userTheme = result.response.profile.theme || 'dark';
                themeStore.setTheme(userTheme as 'light' | 'dark');

                console.log('User profile fetched and stored.');
            } else {
                // Token invalid or profile fetch failed
                console.log('Token validation failed or profile not found:', result.response.response.reason);
                setTokenStorage(undefined);
                appStore.setToken('');
                appStore.setProfile(null);
            }
        } catch (e) {
            console.error('Error during token validation or profile fetch:', e);
            setTokenStorage(undefined);
            appStore.setToken('');
            appStore.setProfile(null);
        }
    }    
</script>

<div class="app">
	<Router {routes} />
</div>

<style>
	:global(body) {
		margin: 0;
		padding: 0;
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif;
	}

	.app {
		min-height: 100vh;
	}
</style>